import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowRight, Sparkles, Activity, BookOpen, Brain, Target,
  Trophy, ShieldCheck, Stethoscope, GraduationCap, Heart,
  Crown, Gem, Flame, Headphones, Image as ImageIcon,
} from "lucide-react";
import MotionButton from "../components/motion/MotionButton";
import MotionCard from "../components/motion/MotionCard";
import GlassPanel from "../components/motion/GlassPanel";
import DailyQuestions from "../components/DailyQuestions";
import AcademiaTeaserBanner from "../components/AcademiaTeaserBanner";
import BookLaunchSection from "../components/BookLaunchSection";
import BookBurocraziaSection from "../components/BookBurocraziaSection";
import {
  EASING, FADE_UP_VIEWPORT,
  HERO_TITLE_PARENT, HERO_TITLE_LINE,
} from "../lib/motionVariants";

const LOGO = "https://customer-assets.emergentagent.com/job_italian-med-test/artifacts/5mezey23_Logo%20MPI.jpeg";
const HERO_ARCH = "https://images.pexels.com/photos/35909639/pexels-photo-35909639.jpeg";
const STETHOSCOPE = "https://images.pexels.com/photos/48604/pexels-photo-48604.jpeg";

const MANDATORY_COPY = "Supera l'esame di riconoscimento della laurea in medicina in Italia";

const HERO_LINES = ["Supera l'esame", "di", "riconoscimento", "della laurea in medicina", "in Italia."];
const HERO_BADGE_TR = { duration: 0.7, ease: EASING };
const HERO_PARAGRAPH_TR = { duration: 0.7, delay: 0.6 };
const HERO_CTAS_TR = { duration: 0.7, delay: 0.85 };
const HERO_TRUST_TR = { delay: 1.1, duration: 0.8 };
const HERO_GLASS_TR = { duration: 0.8, delay: 1, ease: EASING };
const HERO_GLASS_INIT = { opacity: 0, y: 40 };
const HERO_GLASS_ANIM = { opacity: 1, y: 0 };

const FEATURES = [
  { Icon: Activity, t: "Simulazione esame", d: "Cronometrata, identica al test ministeriale. Risultato e analisi per specialità." },
  { Icon: BookOpen, t: "Allenamento libero", d: "Pratica con feedback immediato e spiegazione dopo ogni risposta." },
  { Icon: Sparkles, t: "Tutor AI · GPT-5.2", d: "Chiedi al tutor di spiegarti la risposta come fosse un docente di medicina." },
  { Icon: Brain, t: "Ripetizione spaziata", d: "Algoritmo SM-2: rivedi al momento giusto solo ciò che rischi di dimenticare." },
  { Icon: Target, t: "I tuoi errori", d: "Sezione dedicata per fissare le domande sbagliate con tecniche scientifiche." },
  { Icon: Trophy, t: "Statistiche complete", d: "Dashboard con accuracy, performance per specialità e aree deboli." },
];
const STATS = [
  { v: "2.191", l: "domande" },
  { v: "20", l: "specialità" },
  { v: "91%", l: "pass rate" },
  { v: "1.4k", l: "medici" },
];

const handleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function HeroBackdrop({ y }) {
  return (
    <motion.div style={{ y }} className="absolute inset-0 -top-10 -bottom-10 pointer-events-none" aria-hidden="true">
      <div className="relative h-full w-full">
        <img
          src={HERO_ARCH}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-center scale-110"
          draggable="false"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-background via-brand-background/85 to-brand-background/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-brand-background/95" />
      </div>
    </motion.div>
  );
}

function HeroHalos() {
  return (
    <>
      <div className="absolute -top-32 right-0 w-[420px] h-[420px] rounded-full bg-brand-green/10 blur-[110px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[360px] h-[360px] rounded-full bg-brand-red/8 blur-[120px] pointer-events-none" />
    </>
  );
}

function NavCTA() {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user) {
    return (
      <MotionButton data-testid="header-dashboard-btn" variant="primary" size="sm" onClick={() => navigate("/dashboard")}>
        Dashboard <ArrowRight className="h-3.5 w-3.5" />
      </MotionButton>
    );
  }
  return (
    <MotionButton data-testid="header-login-btn" variant="secondary" size="sm" onClick={handleLogin}>
      Accedi con Google
    </MotionButton>
  );
}

function HeroNav() {
  return (
    <nav className="relative z-10 px-6 sm:px-12 pt-6 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="h-11 w-11 rounded-full bg-white border border-brand-border overflow-hidden shadow-premium flex items-center justify-center">
          <img src={LOGO} alt="" className="h-full w-full object-cover scale-[1.6] -translate-y-[6%]" />
        </span>
        <div>
          <div className="font-serif text-base font-semibold tracking-tight text-brand-primary">Medicos para Italia</div>
          <div className="eyebrow text-[10px]">Esame · Italia · 2026</div>
        </div>
      </div>
      <NavCTA />
    </nav>
  );
}

function HeroBadge() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={HERO_BADGE_TR}
      className="inline-flex items-center gap-2 mb-7 px-3 py-1.5 rounded-full bg-white border border-brand-border shadow-premium"
    >
      <span className="h-2 w-2 rounded-full bg-brand-green animate-pulse" />
      <span className="text-xs font-medium text-brand-secondary tracking-wide">
        Powered by GPT-5.2 · Banco di 2.191 domande
      </span>
    </motion.div>
  );
}

function HeroTitleLine({ line, index }) {
  if (index === 2) {
    return <em className="italic-display text-brand-green">{line}</em>;
  }
  if (index === 4) {
    return (
      <>
        in{" "}
        <span className="relative inline-block">
          <span className="relative z-10 italic-display">Italia.</span>
          <span className="absolute bottom-1 left-0 right-0 h-3 bg-brand-red/15 -z-0" />
        </span>
      </>
    );
  }
  return line;
}

function HeroTitle() {
  return (
    <motion.h1
      initial="hidden"
      animate="show"
      variants={HERO_TITLE_PARENT}
      className="heading-display text-5xl sm:text-7xl lg:text-[112px] text-brand-primary max-w-5xl"
    >
      {HERO_LINES.map((line, i) => (
        <motion.span key={`${line}-${i}`} variants={HERO_TITLE_LINE} className="block">
          <HeroTitleLine line={line} index={i} />
        </motion.span>
      ))}
    </motion.h1>
  );
}

function HeroPrimaryCTA() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <MotionButton
      data-testid="hero-cta-start-btn"
      variant="primary"
      size="lg"
      onClick={() => (user ? navigate("/dashboard") : handleLogin())}
      title={MANDATORY_COPY}
    >
      Inizia gratis ora <ArrowRight className="h-4 w-4" />
    </MotionButton>
  );
}

function TrustItem({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-brand-green" />
      <span>{children}</span>
    </div>
  );
}

function HeroBody({ y, opacity }) {
  return (
    <motion.div style={{ y, opacity }} className="relative z-10 px-6 sm:px-12 pt-12 sm:pt-20 lg:pt-28 pb-16 max-w-7xl mx-auto">
      <HeroBadge />
      <HeroTitle />
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={HERO_PARAGRAPH_TR}
        className="mt-10 max-w-xl text-lg leading-relaxed text-brand-secondary"
      >
        Una piattaforma curata da medici stranieri già abilitati in Italia. Domande
        aggiornate, simulazioni cronometrate, tutor AI e ripetizione spaziata —
        tutto pensato per farti arrivare preparato.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={HERO_CTAS_TR}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <HeroPrimaryCTA />
        <a href="#daily" className="gold-link text-sm font-medium uppercase tracking-[0.2em] text-brand-primary">
          Provo le 3 domande del giorno →
        </a>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={HERO_TRUST_TR}
        className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-brand-secondary"
      >
        <TrustItem icon={ShieldCheck}>Pagamenti sicuri Stripe</TrustItem>
        <TrustItem icon={Sparkles}>Spiegazioni IA GPT-5.2</TrustItem>
        <TrustItem icon={Stethoscope}>Curato da medici</TrustItem>
      </motion.div>
    </motion.div>
  );
}

function HeroSnapshotCard() {
  return (
    <motion.div
      initial={HERO_GLASS_INIT}
      animate={HERO_GLASS_ANIM}
      transition={HERO_GLASS_TR}
      className="hidden lg:block absolute right-12 bottom-12 z-10 w-[320px]"
    >
      <GlassPanel className="rounded-2xl p-5">
        <div className="eyebrow mb-3">// Live snapshot</div>
        <div className="grid grid-cols-2 gap-4">
          {STATS.map((it) => (
            <div key={it.l}>
              <div className="font-serif text-3xl font-semibold text-brand-primary leading-none">{it.v}</div>
              <div className="eyebrow mt-1.5">{it.l}</div>
            </div>
          ))}
        </div>
      </GlassPanel>
    </motion.div>
  );
}

function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg  = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.5]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-brand-background min-h-[88vh] lg:min-h-screen"
      data-testid="hero-section"
    >
      <HeroBackdrop y={yImg} />
      <HeroHalos />
      <HeroNav />
      <HeroBody y={yText} opacity={opacity} />
      <HeroSnapshotCard />
    </section>
  );
}

function FeaturesSection() {
  return (
    <section className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
      <motion.div
        initial={FADE_UP_VIEWPORT.initial}
        whileInView={FADE_UP_VIEWPORT.whileInView}
        viewport={FADE_UP_VIEWPORT.viewport}
        transition={{ duration: 0.6 }}
        className="max-w-3xl"
      >
        <div className="eyebrow mb-3">// Funzionalità</div>
        <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl text-brand-primary">
          Tutto ciò che serve. <em className="italic-display text-brand-secondary">Niente di superfluo.</em>
        </h2>
        <p className="mt-5 text-base text-brand-secondary leading-relaxed">
          Ogni dettaglio è pensato per medici stranieri con poco tempo e un obiettivo chiaro:
          superare l'esame al primo tentativo.
        </p>
      </motion.div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map(({ Icon, t, d }, idx) => (
          <MotionCard
            key={t}
            delay={idx * 0.06}
            data-testid={`feature-${t.replace(/\s/g, "-").toLowerCase()}`}
          >
            <div className="h-11 w-11 rounded-xl bg-brand-green/10 text-brand-green flex items-center justify-center mb-5">
              <Icon className="h-5 w-5" />
            </div>
            <div className="font-serif text-xl font-semibold text-brand-primary mb-2">{t}</div>
            <p className="text-sm text-brand-secondary leading-relaxed">{d}</p>
          </MotionCard>
        ))}
      </div>
    </section>
  );
}

function CredoSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <section className="relative bg-brand-surfaceElevated border-y border-brand-border overflow-hidden">
      <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block">
        <img src={STETHOSCOPE} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-surfaceElevated via-brand-surfaceElevated/40 to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="eyebrow mb-3">// Per medici stranieri in Italia</div>
          <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl text-brand-primary">
            Studia con metodo. <br />
            <em className="italic-display text-brand-green">Approva al primo tentativo.</em>
          </h2>
          <p className="mt-6 text-base text-brand-secondary leading-relaxed max-w-xl">
            {MANDATORY_COPY}. La piattaforma analizza i tuoi errori, prioritizza i punti deboli
            e ti propone la prossima domanda al momento esatto in cui il cervello sta per dimenticarla.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <MotionButton
              data-testid="bottom-cta-btn"
              variant="primary"
              size="lg"
              onClick={() => (user ? navigate("/dashboard") : handleLogin())}
            >
              Inizia ora <ArrowRight className="h-4 w-4" />
            </MotionButton>
            <div className="flex items-center gap-1.5 text-xs text-brand-secondary">
              <GraduationCap className="h-4 w-4" />
              7 giorni gratis · senza carta
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FooterSection() {
  return (
    <footer className="relative max-w-7xl mx-auto px-6 sm:px-12 py-12">
      <div className="tricolor-rule mb-8 max-w-[140px]" />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-full bg-white border border-brand-border overflow-hidden">
            <img src={LOGO} alt="" className="h-full w-full object-cover scale-[1.6] -translate-y-[6%]" />
          </span>
          <div>
            <div className="font-serif text-base font-semibold text-brand-primary">Medicos para Italia</div>
            <div className="text-xs text-brand-secondary">Esame di riconoscimento · Ministero della Salute</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-brand-secondary">
          <Heart className="h-4 w-4 text-brand-red" />
          © 2026 Medicos para Italia. Tutti i diritti riservati.
        </div>
      </div>
    </footer>
  );
}

function MedicosBrandSection() {
  return (
    <section className="relative py-20 sm:py-28" data-testid="brand-section">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-surface via-brand-surface to-brand-background" />
      <div className="max-w-5xl mx-auto px-6 sm:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center"
        >
          {/* Shield with glow + tricolor halo */}
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 -z-10 flex items-center justify-center">
              <div className="h-72 w-72 rounded-full bg-gradient-to-br from-brand-green/20 via-brand-gold/15 to-brand-red/20 blur-3xl" />
            </div>
            <motion.img
              data-testid="brand-shield-img"
              src="/img/medicos-para-italia-shield.jpeg"
              alt="Stemma Medicos para Italia"
              className="w-64 sm:w-80 h-auto object-contain drop-shadow-[0_20px_40px_rgba(17,42,30,0.18)] rounded-2xl"
              initial={{ scale: 0.92, rotate: -3 }}
              whileInView={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.04, rotate: 1 }}
            />
          </div>

          {/* Copy + CTA */}
          <div>
            <div className="eyebrow text-brand-gold mb-3">// Medicos para Italia</div>
            <h2 className="heading-display text-3xl sm:text-4xl text-brand-primary leading-[1.05]">
              La preparazione completa <em className="not-italic text-brand-red">non si ferma qui.</em>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-secondary leading-relaxed">
              Visita il nostro sito ufficiale per scoprire tutto il percorso:
              consulenze personalizzate, manuale di teoria su Amazon, guide al
              riconoscimento del titolo e una community di medici stranieri che
              hanno già superato l'esame in Italia.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a
                href="https://www.medicosparaitalia.it"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="medicos-website-link"
              >
                <MotionButton variant="primary" size="lg">
                  Entra nel sito ufficiale
                  <ArrowRight className="h-4 w-4" />
                </MotionButton>
              </a>
              <span className="text-xs text-brand-secondary tracking-wide">
                www.medicosparaitalia.it
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── VIP HERO · Premium promotional section for Academia VIP ────────────────
// Designed for maximum visual salience: red→gold animated glow card on a deep
// charcoal canvas with golden grain texture. Positioned right after the daily-
// questions widget so visitors see it before the generic features grid.

const VIP_PERKS = [
  { Icon: Headphones, t: "Audio MP3 IT/ES", d: "32+ ore di lezioni narrate, ogni argomento del programma" },
  { Icon: ImageIcon,  t: "Atlante illustrato", d: "Immagini cliniche HD generate per ogni concetto" },
  { Icon: GraduationCap, t: "37 lezioni · 6 moduli", d: "Programma ministeriale completo, bilingue" },
  { Icon: Trophy, t: "Simulatore esame reale", d: "Modalità ufficiale con timer e certificato" },
];

function VIPHeroSection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const cta = user ? () => navigate("/vip") : handleLogin;
  return (
    <section
      data-testid="landing-vip-hero"
      className="relative overflow-hidden py-20 sm:py-28"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(220,38,38,0.18) 0%, transparent 55%)," +
          "radial-gradient(ellipse at bottom right, rgba(234,179,8,0.18) 0%, transparent 55%)," +
          "linear-gradient(180deg, #0E0A1F 0%, #1B1235 50%, #120821 100%)",
      }}
    >
      {/* Subtle grain overlay for premium texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.045] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27240%27 height=%27240%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27/></filter><rect width=%27240%27 height=%27240%27 filter=%27url(%23n)%27/></svg>")',
        }}
      />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-12">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: EASING }}
          className="flex items-center justify-center gap-2 mb-5 flex-wrap"
        >
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold"
                style={{ background: "linear-gradient(90deg, #DC2626, #EAB308)",
                         color: "#FFF8E7", boxShadow: "0 2px 12px rgba(220,38,38,0.4)" }}>
            <Flame className="h-3 w-3" /> Esclusivo
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold"
                style={{ background: "rgba(234,179,8,0.15)", color: "#FCD34D",
                         border: "1px solid rgba(234,179,8,0.4)" }}>
            <Gem className="h-3 w-3" /> Premium VIP
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold"
                style={{ background: "rgba(176,156,255,0.15)", color: "#C4B5FD",
                         border: "1px solid rgba(176,156,255,0.35)",
                         animation: "vip-badge-twinkle 2.6s ease-in-out infinite" }}>
            <Sparkles className="h-3 w-3" /> Nuovo
          </span>
        </motion.div>

        {/* Title + subtitle */}
        <motion.h2
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASING, delay: 0.1 }}
          className="text-center font-serif tracking-tight"
          style={{
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: 1.05,
            color: "#FFF8E7",
            textShadow: "0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          ACADEMIA VIP <br className="hidden sm:block" />
          <span style={{
            background: "linear-gradient(90deg, #FCA5A5 0%, #FCD34D 50%, #FCA5A5 100%)",
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: "vip-shimmer 5s ease-in-out infinite",
          }}>
            Preparazione completa
          </span>
          <span className="block text-[0.6em] font-sans font-normal opacity-90 mt-2"
                style={{ color: "#E2D7B8" }}>
            per esercitare come medico in Italia
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASING, delay: 0.25 }}
          className="text-center mx-auto max-w-2xl mt-6 text-base sm:text-lg leading-relaxed"
          style={{ color: "rgba(255,248,231,0.78)" }}
        >
          Accedi al contenuto più avanzato della piattaforma · lezioni esclusive,
          materiale premium e allenamento reale per accelerare il tuo percorso
          in Italia.
        </motion.p>

        {/* PERKS cards (4-up grid, glass on dark) */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.45 } },
          }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-12 sm:mt-14"
        >
          {VIP_PERKS.map(({ Icon, t, d }) => (
            <motion.div
              key={t}
              variants={{
                hidden: { opacity: 0, y: 18 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASING } },
              }}
              className="vip-hero-card relative rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1"
              style={{
                background: "rgba(20, 14, 40, 0.55)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                border: "1px solid rgba(234,179,8,0.18)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              }}
            >
              <div className="inline-flex items-center justify-center h-9 w-9 rounded-xl mb-3"
                   style={{ background: "linear-gradient(135deg, #DC2626, #EAB308)",
                            color: "#FFF8E7" }}>
                <Icon className="h-4 w-4" />
              </div>
              <h3 className="text-sm font-serif font-semibold mb-1"
                  style={{ color: "#FFF8E7" }}>{t}</h3>
              <p className="text-xs leading-relaxed"
                 style={{ color: "rgba(255,248,231,0.65)" }}>{d}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA card with the breathing red→gold glow */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASING, delay: 0.6 }}
          className="vip-hero-card relative mt-12 sm:mt-16 rounded-3xl p-8 sm:p-12 text-center overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(185,28,28,0.55) 0%, rgba(234,179,8,0.45) 100%)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(234,179,8,0.45)",
            animation: "vip-hero-glow 4.5s ease-in-out infinite",
          }}
        >
          <div aria-hidden="true"
               className="absolute -inset-px rounded-3xl pointer-events-none"
               style={{
                 background: "linear-gradient(110deg, transparent 30%, rgba(255,248,231,0.25) 50%, transparent 70%)",
                 backgroundSize: "250% 100%",
                 animation: "vip-sweep 5s linear infinite",
                 mixBlendMode: "overlay",
               }} />

          <div className="relative">
            <Crown className="mx-auto h-10 w-10 mb-4"
                   style={{ color: "#FCD34D",
                            filter: "drop-shadow(0 0 12px rgba(252,211,77,0.6))" }} />
            <h3 className="font-serif text-2xl sm:text-3xl mb-3 leading-tight"
                style={{ color: "#FFF8E7", textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
              Tutto il programma ministeriale,
              <br className="hidden sm:block" /> in un'unica academy
            </h3>
            <p className="text-sm sm:text-base mx-auto max-w-xl mb-7"
               style={{ color: "rgba(255,248,231,0.85)" }}>
              <strong>6 moduli</strong> · <strong>37 lezioni</strong> ·
              <strong> oltre 32 ore</strong> di audio · atlante illustrato
              · simulatore esame reale.
            </p>

            <button
              data-testid="landing-vip-cta"
              onClick={cta}
              className="group inline-flex items-center justify-center gap-2.5 px-7 sm:px-10 py-4 rounded-full text-base sm:text-lg font-bold uppercase tracking-wider transition-all duration-300 hover:scale-105 active:scale-100"
              style={{
                background: "linear-gradient(135deg, #FFF8E7 0%, #FCD34D 100%)",
                color: "#7A1D1D",
                boxShadow:
                  "0 10px 32px -4px rgba(252,211,77,0.55), 0 0 0 1px rgba(252,211,77,0.3) inset",
              }}
            >
              <Crown className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-8deg]" />
              Entra nell'Academia VIP
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            <p className="text-[11px] uppercase tracking-[0.2em] mt-5"
               style={{ color: "rgba(255,248,231,0.65)" }}>
              Prima lezione di ogni modulo · sempre gratuita
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}



export default function Landing() {
  return (
    <div className="paper text-brand-primary min-h-screen relative">
      <HeroSection />
      <DailyQuestions />
      <VIPHeroSection />
      <FeaturesSection />
      <BookBurocraziaSection />
      <BookLaunchSection />
      <CredoSection />
      <MedicosBrandSection />
      <AcademiaTeaserBanner ctaHref="/vip" testId="landing-academia-teaser" />
      <FooterSection />
    </div>
  );
}
